import { useState, useMemo, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';

// ==================== 2. MUI Material & External Libraries ====================
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';

// ==================== 3. Icons ====================
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import EyeOutlined from '@ant-design/icons/EyeOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import { CommonFilemanagerFileIcon } from 'components/CommonFileManager/CommonFilemanagerIcon';
import PdfPreview from 'components/CommonFileManager/PdfPreview';
import { ModernCompletedIcon, ModernIconExpandDown, ModernIconExpandUp, ModernPendingIcon, } from 'utils/icons';
import {
  CompleteChipIcon,
  DueCalanderIcon,
  GreenTickIcon,
  HighFlagIcon,
  IconExpandDown,
  IconExpandUp,
  InprogressChipIcon,
  ModerCityIcon,
  ModerIconContractorView,
  ModerPaperIcon,
  ModerPermitIssueIcon,
  ModernCityAprrovedIcon,
  ModernCityReviewIcon,
  ModernFinalClouser,
  ModernIconMore,
  ModernInspectionIcon,
  ModernIntakeIcon,
  ModernPlanReviewer,
  ModernTATIcon,
  OverDueCalanderIcon,
  PendingChipIcon,
  PriorityFlagIcon,
  TasknameInprogressIcon,
  UrgentFlagIcon,
  UserviewIcon
} from 'utils/TaskManagementIcon/taskmanagemeneticon';

// ==================== 4. API Imports ====================
import { useGetTaskNoteList, useDeleteTaskNote, useGetTaskNoteFiles, useGetNoteFileUrl } from 'api/TaskManagement/TaskNote/taskNote';
import { useUpdateTaskStatus, useGetTaskFiles, useGetFileUrl } from 'api/TaskManagement/taskManagement';
import { useGetPPFProjectByID, useUpdateProjectStageSummary } from 'api/PPFUser/ppfProject';

// ==================== 5. Types, Utils & Components ====================
import { getStatusEnumVal, TaskCardProps, TaskNote, TaskStage, TaskStatusEnum } from '../../types/taskManagement/TaskManagement';
import { GetFormattedDateOnly, GetFormattedDateTime, GetFormattedTimeOnly, GetFriendlyTaskDueDate, stripAndDecodeHtml, cleanNoteHtml } from 'utils/commonFunctions';
import { showError } from 'utils/errorHandler';
import NoteForm from './NoteForm';
import ViewTaskForm from './ViewTaskForm';
import DeleteTaskModal from './DeleteTaskModal';
import DeleteNoteModal from './DeleteNoteModal';
import CancelModal from './CancelModal';
import TATForm from './TATForm';
import TatChip from 'components/TaskManagement/TatChip';
import { deriveStatusFromDates } from 'utils/tatUtils';
import { CloseOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { mutate } from 'swr';
import { ModernSubmittedToCityIcon } from 'utils/taskTemplateIcons/icons';
import { height } from '@mui/system';
export const getAvatarThemeColor = (name: string) => {
  const avatarColors = [
    '#46A4E7', // soft blue
    '#E7A946', // soft orange
    '#46E79E', // soft green
    '#9E46E7', // soft purple
    '#E7465D'  // soft red
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return avatarColors[Math.abs(hash) % avatarColors.length];
};

// Helper to render Stage icon from taskmanagemeneticon
export const renderStageIcon = (stage?: TaskStage) => {
  switch (stage) {
    case TaskStage.INTAKE:
      return <ModernIntakeIcon />;
    case TaskStage.SUBMITTED_TO_CITY:
      return <ModerCityIcon />;
    case TaskStage.PAPERWORK:
      return <ModerPaperIcon />;
    case TaskStage.PERMIT_ISSUED:
      return <ModerPermitIssueIcon />;
    case TaskStage.CITY_REVIEW:
      return <ModernCityReviewIcon />;
    case TaskStage.CITY_APPROVED:
      return <ModernCityAprrovedIcon />;
    case TaskStage.INSPECTIONS:
      return <ModernInspectionIcon />;
    case TaskStage.PLAN_REVIEW:
      return <ModernPlanReviewer />;
    case TaskStage.FINAL_CLOSURE:
      return <ModernFinalClouser />;
    default:
      return <ModerPaperIcon />;
  }
};

// =========================================================
// [NOTE FILES] Component & functions to fetch & display files for a NOTE
// =========================================================
export const TaskCardNoteAttachmentChips = ({ taskNoteID, initialFiles = [] }: { taskNoteID: number | string; initialFiles?: any[] }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [pdfPreviewState, setPdfPreviewState] = useState<{ url: string; title: string } | null>(null);

  // Hook to fetch files for a specific NOTE (by taskNoteID)
  const { data: fetchedNoteFiles, isLoading: isNoteFilesLoading, isValidating: isNoteFilesValidating } = useGetTaskNoteFiles(taskNoteID);

  const attachments = useMemo(() => {
    const rawFiles = Array.isArray(fetchedNoteFiles) && fetchedNoteFiles.length > 0
      ? fetchedNoteFiles
      : initialFiles;

    return rawFiles
      .map((f: any) => ({
        id: String(f.fileID || f.id || f.fileId || f.primaryKeyID || ''),
        name: f.fileName || f.name || f.filename || 'File',
        size: f.size || f.fileSize || 0
      }))
      .filter((f: any) => Boolean(f.id) && f.id !== 'undefined' && Boolean(f.name));
  }, [fetchedNoteFiles, initialFiles]);

  const hasInitialFiles = Array.isArray(initialFiles) && initialFiles.length > 0;

  if (isNoteFilesLoading && hasInitialFiles && (!fetchedNoteFiles || (Array.isArray(fetchedNoteFiles) && fetchedNoteFiles.length === 0))) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.75, py: 0.25 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.65,
            px: 1.1,
            py: 0.35,
            borderRadius: '50px',
            bgcolor: isDark ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.primary.main, 0.06),
            border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
            boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.08)}`
          }}
        >
          <Box
            sx={{
              width: 11,
              height: 11,
              borderRadius: '50%',
              border: `2px solid ${alpha(theme.palette.primary.main, 0.25)}`,
              borderTopColor: theme.palette.primary.main,
              animation: 'spinCircle 0.8s linear infinite',
              '@keyframes spinCircle': {
                '0%': { transform: 'rotate(0deg)' },
                '100%': { transform: 'rotate(360deg)' }
              }
            }}
          />
          <Typography
            variant="caption"
            sx={{
              fontSize: '11px',
              fontWeight: 600,
              color: 'primary.main',
              letterSpacing: '0.15px'
            }}
          >
            Loading files...
          </Typography>
        </Box>
      </Stack>
    );
  }

  if (!attachments || attachments.length === 0) return null;

  // Function to download or preview a NOTE file
  const handleDownloadNoteFile = async (fileID: string, fileName?: string) => {
    try {
      setDownloadingFileId(String(fileID));
      const res = await useGetNoteFileUrl({ taskNoteID, fileID });
      const fileUrl = typeof res === 'string'
        ? res
        : (res?.fileUrl || res?.url || res?.data?.fileUrl || res?.data?.url || (typeof res?.data === 'string' ? res.data : null));
      if (fileUrl) {
        const isPdf = fileName ? fileName.toLowerCase().endsWith('.pdf') : fileUrl.toLowerCase().includes('.pdf');
        if (isPdf) {
          setPdfPreviewState({ url: fileUrl, title: fileName || 'PDF Preview' });
          return;
        }
        try {
          const response = await fetch(fileUrl);
          if (!response.ok) throw new Error('Download failed');
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          if (fileName) link.download = fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        } catch {
          const link = document.createElement('a');
          link.href = fileUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          if (fileName) link.download = fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      } else {
        showError('Download URL not available');
      }
    } catch (err) {
      console.error('Note file download error:', err);
    } finally {
      setDownloadingFileId(null);
    }
  };

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center" sx={{ mt: 0.75 }}>
      {attachments.slice(0, 3).map((nFile: any) => {
        const isDownloading = downloadingFileId === String(nFile.id);
        const isPdf = nFile.name ? nFile.name.toLowerCase().endsWith('.pdf') : false;
        return (
          <Chip
            key={nFile.id}
            title={nFile.name}
            icon={
              isDownloading ? (
                <CircularProgress size={12} thickness={5} color="primary" sx={{ ml: 0.75, mr: -0.25 }} />
              ) : (
                <CommonFilemanagerFileIcon name={nFile.name} type="file" />
              )
            }
            label={isDownloading ? (isPdf ? nFile.name : 'Downloading...') : nFile.name}
            disabled={isDownloading}
            variant="outlined"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              if (!isDownloading) {
                handleDownloadNoteFile(nFile.id, nFile.name);
              }
            }}
            sx={{
              height: 26,
              bgcolor: 'background.paper',
              borderColor: 'divider',
              borderRadius: 1.5,
              fontSize: '11.5px',
              color: 'text.primary',
              fontWeight: 500,
              cursor: isDownloading ? 'default' : 'pointer',
              transition: 'all 0.2s',
              '& .MuiChip-icon': { ml: 0.75, mr: -0.25 },
              '& .MuiChip-label': { px: 0.75 },
              '&:hover': { bgcolor: 'grey.50', borderColor: 'primary.light' },
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          />
        );
      })}
      {attachments.length > 3 && (
        <Tooltip
          title={
            <Stack spacing={0.75} sx={{ p: 0.5 }}>
              {attachments.slice(3).map((nFile: any) => {
                const isDownloading = downloadingFileId === String(nFile.id);
                const isPdf = nFile.name ? nFile.name.toLowerCase().endsWith('.pdf') : false;
                return (
                  <Chip
                    key={nFile.id}
                    icon={
                      isDownloading ? (
                        <CircularProgress size={12} thickness={5} color="primary" sx={{ ml: 0.5, mr: -0.5 }} />
                      ) : (
                        <CommonFilemanagerFileIcon name={nFile.name} type="file" />
                      )
                    }
                    label={isDownloading ? (isPdf ? nFile.name : 'Downloading...') : nFile.name}
                    disabled={isDownloading}
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isDownloading) {
                        handleDownloadNoteFile(nFile.id, nFile.name);
                      }
                    }}
                    sx={{
                      height: 26,
                      bgcolor: 'grey.50',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      fontSize: '11.5px',
                      color: 'text.primary',
                      fontWeight: 500,
                      cursor: isDownloading ? 'default' : 'pointer',
                      '& .MuiChip-icon': { ml: 0.5, mr: -0.5 },
                      '& .MuiChip-label': { pl: 0.5, pr: 0.5 },
                      '&:hover': { bgcolor: 'grey.100', borderColor: 'primary.light' }
                    }}
                  />
                );
              })}
            </Stack>
          }
          placement="bottom"
          arrow
          enterTouchDelay={0}
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: 'background.paper',
                color: 'text.primary',
                boxShadow: (theme: any) => theme.customShadows?.z1 || '0px 6px 20px rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                border: '1px solid',
                borderColor: 'divider',
                p: 1.25,
                minWidth: 150
              }
            },
            arrow: {
              sx: {
                color: 'background.paper',
                '&::before': {
                  border: '1px solid',
                  borderColor: 'divider',
                  backgroundColor: 'background.paper'
                }
              }
            }
          }}
        >
          <Chip
            label={`+${attachments.length - 3} more`}
            size="small"
            variant="outlined"
            sx={{
              height: 24,
              fontSize: '11px',
              bgcolor: 'grey.100',
              borderColor: 'divider',
              color: 'text.primary',
              fontWeight: 500,
              cursor: 'pointer',
              '&:hover': { bgcolor: 'grey.200' }
            }}
          />
        </Tooltip>
      )}

      {/* PDF Preview Modal */}
      {Boolean(pdfPreviewState) && (
        <PdfPreview
          pdfUrl={pdfPreviewState?.url || null}
          pdfTitle={pdfPreviewState?.title || 'PDF Preview'}
          onClose={() => setPdfPreviewState(null)}
        />
      )}
    </Stack>
  );
};

export default function TaskCard({ task, allowAdd, allowEdit, allowDelete, allowView, onView, onEdit, onDelete, onAddNote }: TaskCardProps) {

  const theme = useTheme();
  const location = useLocation();
  const { projectID } = useParams();
  const isPlanReview = location.pathname.toLowerCase().includes('planreviews');
  const isDark = theme.palette.mode === 'dark';
  const canEditTask = Boolean(allowEdit && task.canEdit !== false);
  const canDeleteTask = Boolean(allowDelete && task.canDelete !== false);
  const [currentStatus, setCurrentStatus] = useState<string>(task.status);
  const [currentUpdatedOn, setCurrentUpdatedOn] = useState<string | null>(task.updatedOn ?? task.updatedBy?.updatedDate ?? null);

  useEffect(() => {
    setCurrentStatus(task.status);
  }, [task.status]);

  useEffect(() => {
    setCurrentUpdatedOn(task.updatedOn ?? task.updatedBy?.updatedDate ?? null);
  }, [task.updatedOn, task.updatedBy?.updatedDate]);
  const [expandedNotes, setExpandedNotes] = useState<boolean>(false);
  const [openAddNote, setOpenAddNote] = useState<boolean>(false);
  const [openViewTask, setOpenViewTask] = useState<boolean>(false);
  const [openDeleteTask, setOpenDeleteTask] = useState<boolean>(false);
  const [openCancelTask, setOpenCancelTask] = useState<boolean>(false);

  const [openUpdateTAT, setOpenUpdateTAT] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const [noteMenuAnchor, setNoteMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedNoteId, setSelectedNoteId] = useState<string | null>(null);
  const isNoteMenuOpen = Boolean(noteMenuAnchor);

  const [editingNote, setEditingNote] = useState<TaskNote | null>(null);
  const [openDeleteNote, setOpenDeleteNote] = useState<boolean>(false);
  const [deletingNote, setDeletingNote] = useState<TaskNote | null>(null);
  const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null);
  const [pdfPreviewState, setPdfPreviewState] = useState<{ url: string; title: string } | null>(null);

  // Hook to fetch main TASK Notes
  const { data: fetchedNotes, rawNotesData, mutate: mutateNotes } = useGetTaskNoteList(task.taskID, null, Boolean(task.taskID && (expandedNotes || openAddNote || openDeleteNote)));

  // =========================================================
  // [TASK FILES] Hook to fetch files for the main TASK
  // =========================================================
  const { data: fetchedTaskFiles, isLoading: isTaskFilesLoading, isValidating: isTaskFilesValidating, mutate: mutateFiles } = useGetTaskFiles(task.taskID);

  const attachments = useMemo(() => {
    const rawFiles = Array.isArray(fetchedTaskFiles) && fetchedTaskFiles.length > 0
      ? fetchedTaskFiles
      : (task.attachments || (task as any).files || (task as any).taskFiles || []);

    return rawFiles
      .map((f: any) => ({
        id: String(f.fileID || f.id || f.fileId || f.primaryKeyID || ''),
        name: f.fileName || f.name || f.filename || 'File',
        size: f.size || f.fileSize || 0
      }))
      .filter((f: any) => Boolean(f.id) && f.id !== 'undefined' && Boolean(f.name));
  }, [fetchedTaskFiles, task.attachments, (task as any).files, (task as any).taskFiles]);

  // =========================================================
  // [TASK FILES] Function to download or preview a main TASK file
  // =========================================================
  const handleDownloadFile = async (fileID: string, fileName?: string) => {
    try {
      setDownloadingFileId(String(fileID));
      const res = await useGetFileUrl({ taskID: Number(task.taskID), fileID });
      const fileUrl = typeof res === 'string'
        ? res
        : (res?.fileUrl || res?.url || res?.data?.fileUrl || res?.data?.url || (typeof res?.data === 'string' ? res.data : null));
      if (fileUrl) {
        const isPdf = fileName ? fileName.toLowerCase().endsWith('.pdf') : fileUrl.toLowerCase().includes('.pdf');
        if (isPdf) {
          setPdfPreviewState({ url: fileUrl, title: fileName || 'PDF Preview' });
          return;
        }
        try {
          const response = await fetch(fileUrl);
          if (!response.ok) throw new Error('Download failed');
          const blob = await response.blob();
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          if (fileName) link.download = fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(blobUrl);
        } catch {
          const link = document.createElement('a');
          link.href = fileUrl;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          if (fileName) link.download = fileName;
          document.body.appendChild(link);
          link.click();
          link.remove();
        }
      } else {
        showError('Download URL not available');
      }
    } catch (err) {
      console.error('File download error:', err);
    } finally {
      setDownloadingFileId(null);
    }
  };

  const notes = useMemo(() => {
    const rawList = rawNotesData !== undefined && Array.isArray(rawNotesData) ? rawNotesData : (task.notes || []);
    return rawList.map((item: any) => {
      const name = item.createdByUserName || item.authorName || 'User';
      const initials = item.authorInitials || (name ? name.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) : 'U');
      const dateFormatted = item.createdOn
        ? `${GetFormattedDateOnly(item.createdOn)} on ${GetFormattedTimeOnly(item.createdOn)}`
        : item.createdDate || '';

      const rawNoteFiles = item.attachments || item.files || item.taskNoteFiles || item.taskFiles || item.getFiles || [];
      const noteAttachments = Array.isArray(rawNoteFiles)
        ? rawNoteFiles.map((f: any) => ({
          id: String(f.id || f.fileID || f.fileId || f.primaryKeyID || ''),
          name: f.name || f.fileName || f.filename || 'File',
          size: f.size || f.fileSize || 0
        }))
        : [];

      return {
        id: String(item.taskNoteID || item.primaryKeyID || item.id),
        taskNoteID: item.taskNoteID || item.primaryKeyID || item.id,
        authorName: name,
        authorInitials: initials,
        createdDate: dateFormatted,
        rawCreatedOn: item.createdOn || item.createdDate,
        updatedOn: item.updatedOn || null,
        updatedByUserName: item.updatedByUserName || null,
        noteText: item.noteFormatted || item.notePlain || item.noteText || '',
        notePlain: item.notePlain || '',
        noteFormatted: item.noteFormatted || '',
        attachments: noteAttachments,
        canEdit: item.canEdit ?? true,
        canDelete: item.canDelete ?? true
      };
    });
  }, [fetchedNotes, task.notes]);

  const latestNote = useMemo(() => {
    if (!notes || notes.length === 0) return null;
    if (notes.length === 1) return notes[0];
    return [...notes].sort((a: any, b: any) => {
      const timeA = a.rawCreatedOn ? new Date(a.rawCreatedOn).getTime() : 0;
      const timeB = b.rawCreatedOn ? new Date(b.rawCreatedOn).getTime() : 0;
      if (timeA !== timeB && !isNaN(timeA) && !isNaN(timeB)) {
        return timeB - timeA;
      }
      const idA = Number(a.taskNoteID || a.id || 0);
      const idB = Number(b.taskNoteID || b.id || 0);
      return idB - idA;
    })[0];
  }, [notes]);

  const selectedNote = useMemo(() => {
    return notes.find((n: any) => String(n.id) === String(selectedNoteId));
  }, [notes, selectedNoteId]);

  const handleOpenNoteMenu = (event: React.MouseEvent<HTMLElement>, noteId: string) => {
    event.stopPropagation();
    setNoteMenuAnchor(event.currentTarget);
    setSelectedNoteId(noteId);
  };

  const handleCloseNoteMenu = () => {
    setNoteMenuAnchor(null);
    setSelectedNoteId(null);
  };

  const handleEditNote = () => {
    const targetNote = notes.find((n: any) => String(n.id) === String(selectedNoteId));
    if (targetNote) {
      setEditingNote(targetNote as any);
      setOpenAddNote(true);
    }
    handleCloseNoteMenu();
  };

  const handleDeleteNote = () => {
    const targetNote = notes.find((n: any) => String(n.id) === String(selectedNoteId));
    if (targetNote) {
      setDeletingNote(targetNote as any);
      setOpenDeleteNote(true);
    }
    handleCloseNoteMenu();
  };

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleViewTask = () => {
    handleCloseMenu();
    setOpenViewTask(true);
    if (onView) onView(task);
  };

  const handleEditTask = () => {
    handleCloseMenu();
    if (onEdit) onEdit(task);
  };

  const handleDeleteTask = () => {
    handleCloseMenu();
    setOpenDeleteTask(true);
  };

  const handleUpdateStatus = async (statusEnum: TaskStatusEnum) => {
    handleCloseMenu();
    const prevStatus = currentStatus;
    const prevUpdatedOn = currentUpdatedOn;
    const statusMap: Record<number, string> = {
      0: 'Pending',
      1: 'In-Progress',
      2: 'Completed',
      3: 'Cancelled'
    };
    const newStatus = statusMap[statusEnum] || 'Pending';
    setCurrentStatus(newStatus);

    const taskUpdatedOn = currentUpdatedOn || task.updatedOn || task.updatedBy?.updatedDate || null;

    try {
      const res: any = await useUpdateTaskStatus({
        taskID: Number(task.taskID),
        taskStatus: statusEnum,
        updatedOn: taskUpdatedOn
      });

      const responseUpdatedOn =
        res?.updatedOn || res?.data?.updatedOn ||

        (typeof res?.data === 'string' ? res.data : (typeof res === 'string' && res.includes('T') ? res : null));

      if (responseUpdatedOn && typeof responseUpdatedOn === 'string') {
        setCurrentUpdatedOn(responseUpdatedOn);
        task.updatedOn = responseUpdatedOn;
        if (task.updatedBy) {
          task.updatedBy.updatedDate = responseUpdatedOn;
        }
      }

      if (task.projectID && location.pathname.includes("ppf/projects")) {
        useGetPPFProjectByID(task.projectID)
      }
      if (task.projectID && location.pathname.includes('ppf/projects/workspace')) {
        const nextStageID = res?.additionalResponseData?.NextInProgessTask?.StageID || null
        const stageIDs = nextStageID ? [task.stageID, nextStageID] : [task.stageID]
        useUpdateProjectStageSummary([...new Set(stageIDs)], task.projectID)
      }
      if (task.projectID && location.pathname.includes('ppf/planReviews')) {
        await mutate(`Project/PlanReview/GetTaskSummary/${task.projectID}`);
      }

      if (location.pathname === '/ppf/taskList') {
        mutate((key: any) => typeof key === 'string' ? key.includes('Task/GetList') : Array.isArray(key) && key[0]?.includes('Task/GetList'));
      }

      mutate((key: any) => {
        if (!key) return false;
        const keyStr = typeof key === 'string' ? key : (Array.isArray(key) ? key[0] : JSON.stringify(key));
        return keyStr.includes('/TaskBoard') || keyStr.includes('Task/GetPanelList');
      });
    } catch {
      setCurrentStatus(prevStatus);
      setCurrentUpdatedOn(prevUpdatedOn);
    }
  };

  const handleOpenCancelModal = () => {
    handleCloseMenu();
    setOpenCancelTask(true);
  };

  const handleToggleNotes = () => {
    setExpandedNotes((prev) => !prev);
  };

  // Border color based on task status
  const getBorderColor = () => {
    switch (currentStatus) {
      case 'In-Progress':
        return theme.palette.primary.main;
      case 'Pending':
        return theme.palette.warning.main;
      case 'Completed':
        return theme.palette.success.main;
      case 'Cancelled':
        return theme.palette.error.main;

      default:
        return theme.palette.primary.main;
    }
  };

  ///Open Update ET Model

  const handleUpdateTAT = () => {
    handleCloseMenu();
    setOpenUpdateTAT(true);
  };


  // Helper to render file attachment icon from CommonFileManager
  const renderAttachmentIcon = (fileName: string) => {
    return <CommonFilemanagerFileIcon name={fileName} type="file" />;
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: 1.5,
        p: 1.5,
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderLeft: `4px solid ${getBorderColor()}`,
        bgcolor: 'background.paper',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: theme.customShadows?.z1 || '0 2px 8px rgba(0,0,0,0.08)'
        }
      }}
    >
      <Stack spacing={1}>
        {/* Header Row: Title + Tick/Progress Icon + Status Chip + More Menu */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexGrow: 1, minWidth: 0 }}>
            {currentStatus === 'In-Progress' && (
              canEditTask ? (
                <Tooltip title={"Click to mark this task as Completed"} arrow placement="top">
                  <Box
                    onClick={() => handleUpdateStatus(TaskStatusEnum.Completed)}
                    sx={{
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 0.5,
                      borderRadius: '50%',
                      flexShrink: 0,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(22, 119, 255, 0.2)' : alpha(theme.palette.primary.main, 0.12),
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <TasknameInprogressIcon />
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title={"You don't have permission to mark this task as Completed"} arrow placement="top">
                  <Box
                    sx={{
                      cursor: 'not-allowed',
                      opacity: 0.6,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 0.5,
                      borderRadius: '50%',
                      flexShrink: 0
                    }}
                  >
                    <TasknameInprogressIcon />
                  </Box>
                </Tooltip>
              )
            )}
            {currentStatus === 'Pending' && (
              canEditTask ? (
                <Tooltip title="Click to set this task as In-Progress" arrow placement="top">
                  <Box
                    onClick={() => handleUpdateStatus(TaskStatusEnum.InProgress)}
                    sx={{
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 0.5,
                      borderRadius: '50%',
                      flexShrink: 0,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        bgcolor: isDark ? 'rgba(254, 119, 17, 0.2)' : alpha(theme.palette.warning.main, 0.12),
                        transform: 'scale(1.1)'
                      }
                    }}
                  >
                    <ModernPendingIcon style={{ height: 16, width: 16 }} />
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title={"You don't have permission to set this task as In-Progress"} arrow placement="top">
                  <Box
                    sx={{
                      cursor: 'not-allowed',
                      opacity: 0.6,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      p: 0.5,
                      borderRadius: '50%',
                      flexShrink: 0
                    }}
                  >
                    <ModernPendingIcon style={{ height: 16, width: 16 }} />
                  </Box>
                </Tooltip>
              )
            )}

            {currentStatus === 'Completed' && (
              canEditTask ? (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.5,
                    borderRadius: '50%',
                    flexShrink: 0
                  }}
                >
                  <ModernCompletedIcon />
                </Box>
              ) : (
                <Box
                  sx={{

                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.5,
                    borderRadius: '50%',
                    flexShrink: 0
                  }}
                >
                  <ModernCompletedIcon />
                </Box>
              )
            )}


            {currentStatus === 'Cancelled' && (
              canEditTask ? (
                <Box
                  //onClick={() => handleUpdateStatus(TaskStatusEnum.InProgress)}
                  sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, mr: 1 }}
                >
                  <CloseOutlined style={{ fontSize: 14, color: theme.palette.error.main }} />
                </Box>

              ) : (
                <Box
                  sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0, mr: 1 }}
                >
                  <CloseOutlined style={{ fontSize: 14, color: theme.palette.error.main }} />
                </Box>

              )
            )}


            <Typography
              variant="subtitle1"
              onClick={canEditTask ? handleEditTask : allowView ? handleViewTask : undefined}
              title={canEditTask ? "Click to edit task" : allowView ? "Click to view task" : ""}
              sx={{
                fontWeight: 500,
                fontSize: '15px',
                color: 'primary.main',
                cursor: 'pointer',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                textDecoration: 'underline',
                maxWidth: '250px'
              }}
            >
              {task.title}
            </Typography>

          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {/* Elapsed Time (ET) Live Chip */}
            <TatChip variant='text' startedOn={task.startedOn} completedOn={task.completedOn} elapsedMinutes={task.elapsedMinutes} sx={{ cursor: 'pointer' }} showIcon={false} />
            {/* Stage Chip */}
            {!isPlanReview && task.stage && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  height: 29,
                  px: 1.25,
                  bgcolor: isDark ? 'rgba(22, 119, 255, 0.12)' : 'primary.lighter',
                  color: 'primary.main',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center', color: 'primary.main' }}>
                  {renderStageIcon(task.stage)}
                </Box>
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500, fontSize: '13px', lineHeight: 1 }}>
                  {task.stage}
                </Typography>
              </Box>
            )}

            {currentStatus === 'In-Progress' && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  height: 29,
                  px: 1.25,
                  bgcolor: isDark ? 'rgba(22, 119, 255, 0.12)' : 'primary.lighter',
                  color: 'primary.main',
                  border: `1px solid ${alpha(theme.palette.primary.light, 0.1)}`,
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <InprogressChipIcon />
                </Box>
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 500, fontSize: '13px', lineHeight: 1 }}>
                  In Progress
                </Typography>
              </Box>
            )}
            {currentStatus === 'Pending' && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  height: 29,
                  px: 1.25,
                  bgcolor: isDark ? 'rgba(254, 119, 17, 0.12)' : 'warning.lighter',
                  color: 'warning.main',
                  border: `1px solid ${alpha(theme.palette.warning.light, 0.1)}`,
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <PendingChipIcon />
                </Box>
                <Typography variant="caption" sx={{ color: 'warning.main', fontWeight: 500, fontSize: '13px', lineHeight: 1 }}>
                  Pending
                </Typography>
              </Box>
            )}
            {currentStatus === 'Completed' && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  height: 29,
                  px: 1.25,
                  bgcolor: isDark ? 'rgba(56, 163, 79, 0.12)' : 'success.lighter',
                  color: 'success.main',
                  border: `1px solid ${alpha(theme.palette.success.light, 0.1)}`,
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <ModernCompletedIcon />
                </Box>
                <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500, fontSize: '13px', lineHeight: 1 }}>
                  Completed
                </Typography>
              </Box>
            )}

            {currentStatus === 'Cancelled' && (
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  height: 29,
                  px: 1.25,
                  bgcolor: isDark ? 'rgba(255, 77, 79, 0.12)' : 'error.lighter',
                  color: 'error.main',
                  border: `1px solid ${alpha(theme.palette.error.light, 0.1)}`,
                  borderRadius: '6px',
                  whiteSpace: 'nowrap',
                  flexShrink: 0
                }}
              >
                <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
                  <CloseOutlined />
                </Box>
                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 500, fontSize: '13px', lineHeight: 1 }}>
                  Cancelled
                </Typography>
              </Box>
            )}

            <IconButton onClick={handleOpenMenu} size="small" sx={{ p: 0.5, color: 'text.secondary' }}>
              <ModernIconMore />
            </IconButton>
          </Stack>
        </Stack>

        {/* Container for Task Details (Due date, Description, Attachments) */}
        <Stack spacing={0.75}>
          {/* Row 2: Due Date, Priority Flag, Assigned User */}
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5}>
            {/* Due Date */}
            <Stack direction="row" spacing={0.75} alignItems="center">
              {task.isOverdue && getStatusEnumVal(task.status) !== TaskStatusEnum.Completed ? <OverDueCalanderIcon /> : <DueCalanderIcon />}
              <Typography
                variant="caption"
                fontSize="13px"
                sx={{
                  color: task.isOverdue ? 'error.main' : 'text.secondary',
                  fontWeight: task.isOverdue ? 500 : 400
                }}
              >
                {getStatusEnumVal(task.status) !== TaskStatusEnum.Completed ? task.isOverdue ? 'Overdue : ' : 'Due : ' : null}
                <Box component="span" sx={{ color: 'text.primary', fontSize: '13px', fontWeight: 400 }}>
                  {GetFriendlyTaskDueDate(task.dueDate)}
                </Box>
              </Typography>
            </Stack>

            {/* Thick Vertical Separator */}
            {task.priority && (
              <Box
                sx={{
                  width: '2px',
                  height: 20,
                  bgcolor: 'grey.300',
                  borderRadius: '1px',
                  alignSelf: 'center'
                }}
              />
            )}
            {/* Priority */}
            {task.priority === 'Urgent' && (
              <Chip
                icon={<HighFlagIcon />}
                label="Urgent"
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'divider',
                  height: 24,
                  fontSize: '12px',
                  fontWeight: 400,
                  borderRadius: 1,
                  '& .MuiChip-icon': { ml: 0.5 },
                  p: '10px 6px',
                }}
              />
            )}
            {task.priority === 'High' && (
              <Chip
                icon={<UrgentFlagIcon />}
                label="High"
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'divider',
                  height: 24,
                  fontSize: '12px',
                  fontWeight: 400,
                  borderRadius: 1,
                  '& .MuiChip-icon': { ml: 0.5 },
                  p: '10px 6px',
                }}
              />
            )}

            {task.priority === 'Low' && (
              <Chip
                icon={<PriorityFlagIcon color="#8C8C8C" />}
                label="Low"
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'divider',
                  height: 24,
                  fontSize: '12px',
                  fontWeight: 400,
                  borderRadius: 1,
                  '& .MuiChip-icon': { ml: 0.5 },
                  p: '10px 6px',
                }}
              />
            )}

            {task.priority === 'Normal' && (
              <Chip
                icon={<PriorityFlagIcon color="#1677FF" />}
                label="Normal"
                size="small"
                variant="outlined"
                sx={{
                  borderColor: 'divider',
                  height: 24,
                  fontSize: '12px',
                  fontWeight: 400,
                  borderRadius: 1,
                  '& .MuiChip-icon': { ml: 0.5 },
                  p: '10px 6px',
                }}
              />
            )}
            {task.assignedUser && (
              <>
                <Box
                  sx={{
                    width: '2px',
                    height: 20,
                    bgcolor: 'grey.300',
                    borderRadius: '1px',
                    alignSelf: 'center'
                  }}
                />
              </>
            )}

            {/* Assigned User */}
            {task.assignedUser && (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <UserviewIcon />
                <Typography variant="caption" sx={{ color: 'text.primary', fontSize: '13px', fontWeight: 400 }}>
                  {task.assignedUser.name}
                </Typography>
                {(task.assignedUser.extraCount ?? 0) > 0 && (
                  <Tooltip
                    title={
                      <Stack spacing={0.5}>
                        {(task.assignedUser.extraUsers || []).map((item, index) => (
                          <Typography
                            key={index}
                            variant="body2"
                            sx={{
                              fontSize: '13px',
                              fontWeight: 400,
                              color: 'text.primary',
                              lineHeight: 1.4,
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {item}
                          </Typography>
                        ))}
                      </Stack>
                    }
                    placement="bottom"
                    arrow
                    enterTouchDelay={0}
                    componentsProps={{
                      tooltip: {
                        sx: {
                          bgcolor: 'background.paper',
                          color: 'text.primary',
                          boxShadow: theme.customShadows?.z1 || '0px 6px 20px rgba(0, 0, 0, 0.1)',
                          borderRadius: '8px',
                          border: '1px solid',
                          borderColor: 'divider',
                          p: 1,
                          minWidth: 120
                        }
                      },
                      arrow: {
                        sx: {
                          color: 'background.paper',
                          '&::before': {
                            border: '1px solid',
                            borderColor: 'divider',
                            backgroundColor: 'background.paper'
                          }
                        }
                      }
                    }}
                  >
                    <Chip
                      label={`+${task.assignedUser.extraCount} more`}
                      sx={{
                        height: 20,
                        fontSize: '11.5px',
                        bgcolor: 'primary.lighter',
                        color: 'primary.main',
                        fontWeight: 500,
                        borderRadius: 1,
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        "& .MuiChip-label": {
                          px: 0.5,
                          py: 0.5
                        },
                      }}
                    />
                  </Tooltip>
                )}
              </Stack>
            )}


            {task.visibleToContractor && (
              <>
                <Box
                  sx={{
                    width: '2px',
                    height: 20,
                    bgcolor: 'grey.300',
                    borderRadius: '1px',
                    alignSelf: 'center'
                  }}
                />

                <Stack title="Contractor can view this task" direction="row" spacing={0.75} alignItems="center">
                  <ModerIconContractorView />
                </Stack>
              </>
            )}
          </Stack>

          {/* Row 3: Description (formatted view with proper table & 2-line clamping for long text) */}
          {task.description && (
            <Box
              onClick={canEditTask ? handleEditTask : allowView ? handleViewTask : undefined}
              sx={{
                fontSize: '13.2px',
                lineHeight: 1.5,
                color: 'text.primary',
                overflow: 'hidden',
                maxWidth: '100%',
                wordBreak: 'break-word',
                overflowWrap: 'break-word',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                maxHeight: '60px',
                cursor: 'pointer',
                '& table': {
                  borderCollapse: 'collapse',
                  width: '100%',
                  my: 0.5,
                  '& td, & th': {
                    border: `1px solid ${theme.palette.divider}`,
                    p: 0.5,
                    fontSize: '12px'
                  },
                  '& img': {
                    maxHeight: '36px',
                    maxWidth: '60px',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    verticalAlign: 'middle'
                  }
                },
                '& img': {
                  maxWidth: '100%',
                  maxHeight: '40px',
                  height: 'auto',
                  objectFit: 'contain',
                  borderRadius: 0.5
                },
                '& p': {
                  margin: 0
                }
              }}
              title="Click to view full description and details"
              dangerouslySetInnerHTML={{ __html: task.description }}
            />
          )}

          {/* Row 4: Attachments */}
          {(isTaskFilesLoading && (!fetchedTaskFiles || (Array.isArray(fetchedTaskFiles) && fetchedTaskFiles.length === 0))) ? (
            <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.75,
                  px: 1.25,
                  py: 0.4,
                  borderRadius: '50px',
                  bgcolor: isDark ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.primary.main, 0.06),
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
                  boxShadow: `0 2px 6px ${alpha(theme.palette.primary.main, 0.08)}`,
                  transition: 'all 0.3s ease-in-out'
                }}
              >
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    border: `2px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                    borderTopColor: theme.palette.primary.main,
                    animation: 'spinCircle 0.8s linear infinite',
                    '@keyframes spinCircle': {
                      '0%': { transform: 'rotate(0deg)' },
                      '100%': { transform: 'rotate(360deg)' }
                    }
                  }}
                />
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '11.5px',
                    fontWeight: 600,
                    color: 'primary.main',
                    letterSpacing: '0.2px'
                  }}
                >
                  Loading files...
                </Typography>
              </Box>
            </Stack>
          ) : (
            <Stack direction="row" flexWrap="wrap" gap={0.75} alignItems="center">
              {attachments.length > 0 && attachments.slice(0, 3).map((file) => {
                const isDownloading = downloadingFileId === String(file.id);
                const isPdf = file.name ? file.name.toLowerCase().endsWith('.pdf') : false;
                return (
                  <Chip
                    title={file.name}
                    key={file.id}
                    icon={
                      isDownloading ? (
                        <CircularProgress size={12} thickness={5} color="primary" sx={{ ml: 0.75, mr: -0.25 }} />
                      ) : (
                        <CommonFilemanagerFileIcon name={file.name} type="file" />
                      )
                    }
                    label={isDownloading ? (isPdf ? file.name : 'Downloading...') : file.name}
                    disabled={isDownloading}
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!isDownloading) {
                        handleDownloadFile(String(file.id), file.name);
                      }
                    }}
                    sx={{
                      height: 28,
                      bgcolor: 'grey.50',
                      borderColor: 'divider',
                      borderRadius: 1.5,
                      fontSize: '12px',
                      color: 'text.primary',
                      fontWeight: 500,
                      cursor: isDownloading ? 'default' : 'pointer',
                      transition: 'all 0.2s',
                      '& .MuiChip-icon': {
                        ml: 0.75,
                        mr: -0.25
                      },
                      '& .MuiChip-label': {
                        px: 1
                      },
                      '&:hover': {
                        bgcolor: 'grey.100',
                        borderColor: 'primary.light'
                      },
                      maxWidth: '160px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  />
                );
              })}

              {attachments.length > 3 && (
                <Tooltip
                  title={
                    <Stack spacing={0.5}>
                      {attachments.slice(3).map((file) => {
                        const isDownloading = downloadingFileId === String(file.id);
                        const isPdf = file.name ? file.name.toLowerCase().endsWith('.pdf') : false;
                        return (
                          <Chip
                            key={file.id}
                            icon={
                              isDownloading ? (
                                <CircularProgress size={12} thickness={5} color="primary" sx={{ ml: 0.75, mr: -0.25 }} />
                              ) : (
                                <CommonFilemanagerFileIcon name={file.name} type="file" />
                              )
                            }
                            label={isDownloading ? (isPdf ? file.name : 'Downloading...') : file.name}
                            disabled={isDownloading}
                            variant="outlined"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isDownloading) {
                                handleDownloadFile(String(file.id), file.name);
                              }
                            }}
                            sx={{
                              height: 26,
                              bgcolor: 'grey.50',
                              borderColor: 'divider',
                              borderRadius: 1.5,
                              fontSize: '11.5px',
                              color: 'text.primary',
                              fontWeight: 500,
                              cursor: isDownloading ? 'default' : 'pointer',
                              transition: 'all 0.2s',
                              '& .MuiChip-icon': {
                                ml: 0.75,
                                mr: -0.25
                              },
                              '& .MuiChip-label': {
                                px: 0.75
                              },
                              '&:hover': {
                                bgcolor: 'grey.100',
                                borderColor: 'primary.light'
                              }
                            }}
                          />
                        );
                      })}
                    </Stack>
                  }
                  placement="bottom"
                  arrow
                  enterTouchDelay={0}
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: 'background.paper',
                        color: 'text.primary',
                        boxShadow: theme.customShadows?.z1 || '0px 6px 20px rgba(0, 0, 0, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid',
                        borderColor: 'divider',
                        p: 1,
                        minWidth: 140
                      }
                    },
                    arrow: {
                      sx: {
                        color: 'background.paper',
                        '&::before': {
                          border: '1px solid',
                          borderColor: 'divider',
                          backgroundColor: 'background.paper'
                        }
                      }
                    }
                  }}
                >
                  <Chip
                    label={`+${attachments.length - 3} more`}
                    size="small"
                    sx={{
                      height: 28,
                      fontSize: '12px',
                      bgcolor: 'primary.lighter',
                      color: 'primary.main',
                      fontWeight: 500,
                      borderRadius: 1.5,
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      '& .MuiChip-label': {
                        px: 0.5
                      },
                    }}
                  />
                </Tooltip>
              )}

              {(isTaskFilesLoading || isTaskFilesValidating) && (
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    p: 0.5
                  }}
                >
                  <CircularProgress size={14} thickness={4} color="primary" />
                </Box>
              )}
            </Stack>
          )}
        </Stack>

        {/* Divider separating task info and notes section */}
        <Divider sx={{ my: 0.5, borderColor: theme.palette.divider }} />

        {/* Row 5: Notes Section */}
        {notes && notes.length > 0 ? (
          expandedNotes ? (
            /* EXPANDED STATE */
            <Box
              sx={{
                mt: 1,
                bgcolor: isDark ? '#151515' : 'grey.50',
                p: 0.5,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`
              }}
            >
              {/* Notes Section Header */}
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                onClick={handleToggleNotes}
                sx={{ cursor: 'pointer', mb: 1.5 }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', fontSize: '14px' }}>
                  Notes
                </Typography>

                <IconButton size="small" sx={{ p: 0.25 }}>
                  <IconExpandUp style={{ height: 30, width: 30 }} />
                </IconButton>
              </Stack>

              {/* Notes Content List */}
              <Stack spacing={0}>
                {notes.map((note, index) => (
                  <Box key={note.id}>
                    {index > 0 && <Divider sx={{ my: 0.75, borderColor: 'divider' }} />}
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1} sx={{ mb: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: '10px',
                            bgcolor: getAvatarThemeColor(note.authorName),
                            color: '#fff',
                            fontWeight: 600
                          }}
                        >
                          {note.authorInitials}
                        </Avatar>
                        <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '13px' }}>
                          {note.authorName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                          · {note.createdDate}
                        </Typography>
                      </Stack>
                      {(note.canEdit !== false || note.canDelete !== false) && (
                        <Tooltip title="More Options" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={(e) => handleOpenNoteMenu(e, note.id)}
                            sx={{ p: 0.25, color: 'text.secondary' }}
                          >
                            <ModernIconMore />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>

                    {/* Indented Note Content (Text & attachments aligned under author name) */}
                    <Box sx={{ pl: 4, mt: 0, pr: 1, maxWidth: '100%', wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                      <Typography
                        title={`Note Text - ${stripAndDecodeHtml(note.notePlain || note.noteText)}`}
                        variant="body2"
                        component="div"
                        dangerouslySetInnerHTML={{ __html: cleanNoteHtml(note.noteText) }}
                        sx={{
                          color: "text.secondary",
                          fontSize: '13px',
                          mb: (note.attachments && note.attachments.length > 0) ? 1 : 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          maxWidth: '500px',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                          '& p': { m: 0, p: 0, wordBreak: 'break-word', overflowWrap: 'anywhere' },
                          '& table': {
                            borderCollapse: 'collapse',
                            width: '100%',
                            my: 1,
                            border: '1px solid',
                            borderColor: 'divider'
                          },
                          '& th, & td': {
                            border: '1px solid',
                            borderColor: 'divider',
                            padding: '6px 10px',
                            textAlign: 'left',
                            fontSize: '12px'
                          },
                          '& th': {
                            bgcolor: 'grey.100',
                            fontWeight: 600
                          },
                          '& img': {
                            maxWidth: '100%',
                            height: 'auto',
                            borderRadius: 1,
                            my: 0.5
                          }
                        }}
                      />

                      <TaskCardNoteAttachmentChips taskNoteID={note.taskNoteID} initialFiles={note.attachments} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            </Box>
          ) : (
            /* COLLAPSED STATE*/
            <Box
              onClick={handleToggleNotes}
              sx={{
                mt: 1,
                bgcolor: isDark ? '#151515' : 'grey.50',
                px: 1.25,
                py: 0.5,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: isDark ? '#222222' : 'grey.100'
                }
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '13px',
                  pr: 1,
                  fontWeight: 400,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  wordBreak: 'break-word',
                  overflowWrap: 'anywhere'
                }}
              >
                {stripAndDecodeHtml(latestNote?.notePlain || latestNote?.noteText)}
              </Typography>
              <IconButton size="small" sx={{ p: 0.25, color: 'text.secondary' }}>
                <IconExpandDown style={{ height: "50px", width: "50px" }} />
              </IconButton>
            </Box>
          )
        ) : null}

        {/* Row 6: Card Footer Meta (Add Note on Left, Updated/Created info on Right) */}
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
          {allowAdd ? (
            <Tooltip title="Add Note" placement="top" arrow>
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenAddNote(true);
                  if (onAddNote) onAddNote(task);
                }}
                startIcon={<PlusOutlined style={{ strokeWidth: 2, fontSize: '12px' }} />}
                size="small"
                sx={{
                  color: 'primary.main',
                  textTransform: 'none',
                  fontWeight: 600,
                  p: 0,
                  minWidth: 0,
                  '&:hover': { bgcolor: 'transparent' }
                }}
              >
                Add Note
              </Button>

            </Tooltip>
          ) : null}

          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12.5px', fontWeight: '400', textAlign: 'right' }}>
            {currentStatus === 'Completed' ? 'Completed by ' : (task.updatedBy ? 'Updated by ' : 'Created by ')}
            <Box
              component="span"
              sx={{
                color: 'primary.main',
                fontStyle: 'italic'
              }}
            >
              {currentStatus === 'Completed' ? (task.completedByUserName || (task as any).completedByUserName || task.updatedBy?.authorName || task.createdBy?.authorName) : (task.updatedBy?.authorName || task.createdBy?.authorName)}
            </Box>
            {' on '}
            <Box
              component="span"
              sx={{
                color: 'primary.main',
                fontStyle: 'italic'
              }}
            >
              {GetFormattedDateTime(
                currentStatus === 'Completed'
                  ? (task.completedOn || (task as any).completedOn || currentUpdatedOn || task.updatedBy?.updatedDate || task.createdBy?.createdDate)
                  : (currentUpdatedOn || task.updatedBy?.updatedDate || task.createdBy?.createdDate)
              )}
            </Box>
          </Typography>
        </Stack>
        {/* Task Card Context Menu */}
        <Menu
          anchorEl={anchorEl}
          open={openMenu}
          onClose={handleCloseMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: '12px',
              minWidth: 190,
              p: 0.75,
              border: '1px solid',
              borderColor: theme.palette.grey[300],
              boxShadow: theme.customShadows?.z1 || '0px 8px 24px rgba(0, 0, 0, 0.12)',
              '& .MuiList-root': {
                p: 0
              }
            }
          }}
        >
          {/* Set As Pending - visible when In-Progress, Completed, or Cancelled */}
          {canEditTask && (currentStatus === 'In-Progress' || currentStatus === 'Completed' || currentStatus === 'Cancelled') && (
            <MenuItem
              onClick={() => handleUpdateStatus(TaskStatusEnum.Pending)}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '14px',
                py: 0.75,
                px: 1.5,
                mb: 0.5,
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
            >
              <ClockCircleOutlined style={{ fontSize: 16, marginRight: 10, color: 'inherit' }} />
              Set As Pending
            </MenuItem>
          )}

          {/* Set As In-Progress - visible when In-Progress, Completed, or Cancelled */}
          {canEditTask && (currentStatus === 'Pending' || currentStatus === 'Completed' || currentStatus === 'Cancelled') && (
            <MenuItem
              onClick={() => handleUpdateStatus(TaskStatusEnum.InProgress)}
              sx={{
                borderRadius: '6px',
                fontWeight: 400,
                fontSize: '14px',
                py: 0.75,
                px: 1.5,
                mb: 0.5,
                color: theme.palette.text.primary,
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : theme.palette.grey[200]
                }
              }}
            >
              <Box sx={{ display: 'inline-flex', mr: 1.25, color: theme.palette.text.primary, '& svg': { width: 16, height: 16 } }}>
                <InprogressChipIcon />
              </Box>
              Set As In-Progress
            </MenuItem>
          )}

          {/* Mark As Completed - visible when In-Progress or Pending */}
          {canEditTask && (currentStatus === 'In-Progress' || currentStatus === 'Pending') && (
            <MenuItem
              onClick={() => handleUpdateStatus(TaskStatusEnum.Completed)}
              sx={{
                borderRadius: '6px',
                fontWeight: 400,
                fontSize: '14px',
                py: 0.75,
                px: 1.5,
                mb: 0.5,
                color: theme.palette.text.primary,
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : theme.palette.grey[200]
                }
              }}
            >
              <Box sx={{ display: 'inline-flex', mr: 1.25, color: theme.palette.text.primary, '& svg': { width: 16, height: 16 } }}>
                <CompleteChipIcon />
              </Box>
              Mark As Completed
            </MenuItem>
          )}

          {/* Cancel - visible only when Pending */}
          {canEditTask && currentStatus === 'Pending' && (
            <MenuItem
              onClick={handleOpenCancelModal}
              sx={{
                borderRadius: '6px',
                fontWeight: 400,
                fontSize: '14px',
                py: 0.75,
                px: 1.5,
                mb: 0.5,
                color: theme.palette.text.primary,
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : theme.palette.grey[200]
                }
              }}
            >
              <CloseOutlined style={{ fontSize: 16, marginRight: 10, color: theme.palette.error.main }} />
              Cancelled
            </MenuItem>
          )}
          <Divider />

          {/* Update ET Option - Only visible when status is In-Progress or Completed, and user has edit permission */}
          {canEditTask && (currentStatus === 'In-Progress' || currentStatus === 'Completed') && (
            <MenuItem
              onClick={handleUpdateTAT}
              sx={{
                borderRadius: '6px',
                fontWeight: 400,
                fontSize: '14px',
                py: 0.75,
                px: 1.5,
                mb: 0.5,
                color: theme.palette.text.primary,
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : theme.palette.grey[200]
                }
              }}
            >
              <ModernTATIcon style={{ height: '16px', width: '16px', marginRight: 10, color: theme.palette.info.main }} />
              Update ET
            </MenuItem>
          )}

          {/* View Option */}
          {allowView && !canEditTask && (
            <MenuItem
              onClick={handleViewTask}
              sx={{
                borderRadius: '6px',
                fontWeight: 400,
                fontSize: '14px',
                py: 0.75,
                px: 1.5,
                mb: 0.5,
                color: theme.palette.text.primary,
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : theme.palette.grey[200]
                }
              }}
            >
              <EyeOutlined style={{ fontSize: 16, marginRight: 10, color: theme.palette.info.main }} />
              View
            </MenuItem>
          )}

          {/* Edit Option */}
          {canEditTask && (
            <MenuItem
              onClick={handleEditTask}
              sx={{
                borderRadius: '6px',
                fontWeight: 400,
                fontSize: '14px',
                py: 0.75,
                px: 1.5,
                mb: 0.5,
                color: theme.palette.text.primary,
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : theme.palette.grey[200]
                }
              }}
            >
              <EditOutlined style={{ fontSize: 16, marginRight: 10, color: theme.palette.primary.main }} />
              Edit
            </MenuItem>
          )}

          {/* Delete Option */}
          {canDeleteTask && (
            <MenuItem
              onClick={handleDeleteTask}
              sx={{
                borderRadius: '6px',
                fontWeight: 400,
                fontSize: '14px',
                py: 0.75,
                px: 1.5,
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : theme.palette.grey[200]
                }
              }}
            >
              <DeleteOutlined style={{ fontSize: 16, marginRight: 10, color: theme.palette.error.main }} />
              Delete
            </MenuItem>
          )}
        </Menu>

        {/* Note Context Menu */}
        <Menu
          anchorEl={noteMenuAnchor}
          open={isNoteMenuOpen}
          onClose={handleCloseNoteMenu}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right'
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right'
          }}
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: '12px',
              minWidth: 130,
              p: 0.75,
              border: '1px solid',
              borderColor: theme.palette.grey[300],
              boxShadow: theme.customShadows?.z1 || '0px 8px 24px rgba(0, 0, 0, 0.12)',
              '& .MuiList-root': {
                p: 0
              }
            }
          }}
        >
          {/* Edit Option (Filled Blue Button) */}
          {allowEdit && selectedNote?.canEdit !== false && (
            <MenuItem
              onClick={handleEditNote}
              sx={{
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                borderRadius: '8px',
                fontWeight: 500,
                fontSize: '14px',
                py: 0.85,
                px: 1.5,
                mb: 0.5,
                '&:hover': {
                  bgcolor: 'primary.dark'
                }
              }}
            >
              <EditOutlined style={{ fontSize: 16, marginRight: 10, color: 'inherit' }} />
              Edit
            </MenuItem>
          )}

          {/* Delete Option */}
          {allowDelete && selectedNote?.canDelete !== false && (
            <MenuItem
              onClick={handleDeleteNote}
              sx={{
                color: 'text.primary',
                borderRadius: '6px',
                fontWeight: 400,
                fontSize: '14px',
                py: 0.75,
                px: 1.5,
                '&:hover': {
                  bgcolor: 'action.hover'
                }
              }}
            >
              <DeleteOutlined style={{ fontSize: 16, marginRight: 10, color: theme.palette.error.main }} />
              Delete
            </MenuItem>
          )}
        </Menu>

        {/* Add / Edit Note Modal Form */}
        {openAddNote && (
          <NoteForm
            open={openAddNote}
            closeModal={() => {
              setOpenAddNote(false);
              setEditingNote(null);
              setExpandedNotes(true);
              mutateNotes();
            }}
            taskId={task.taskID}
            selectedNote={editingNote}
          />
        )}

        {/* View Task Modal Form */}
        {openViewTask && (
          <ViewTaskForm
            open={openViewTask}
            closeModal={() => setOpenViewTask(false)}
            task={task}
            onNoteChange={() => {
              setExpandedNotes(true);
              mutateNotes();
            }}
          />
        )}

        {/* Delete Task Modal */}
        {openDeleteTask && (
          <DeleteTaskModal
            open={openDeleteTask}
            onClose={() => setOpenDeleteTask(false)}
            onConfirm={async () => {
              if (onDelete) {
                await onDelete(task);
              }
              setOpenDeleteTask(false);
            }}
            taskTitle={task.title}
            filesCount={attachments?.length || 0}
          />
        )}

        {/* Cancel Task Modal */}
        {openCancelTask && (

          <CancelModal
            open={openCancelTask}
            onClose={() => setOpenCancelTask(false)}
            taskTitle={task.title}
            taskStatus={task.status}
            taskId={task.taskID}
            updatedOn={currentUpdatedOn || task.updatedOn || task.updatedBy?.updatedDate || null}
            onSuccess={(res?: any) => {
              setCurrentStatus('Cancelled');
              const responseUpdatedOn =
                res?.updatedOn || res?.data?.updatedOn ||

                (typeof res?.data === 'string' ? res.data : (typeof res === 'string' && res.includes('T') ? res : null));

              if (responseUpdatedOn && typeof responseUpdatedOn === 'string') {
                setCurrentUpdatedOn(responseUpdatedOn);
                task.updatedOn = responseUpdatedOn;
                if (task.updatedBy) {
                  task.updatedBy.updatedDate = responseUpdatedOn;
                }
              }
              setOpenCancelTask(false);
              if (task.projectID && location.pathname.includes("ppf/projects")) {
                useGetPPFProjectByID(task.projectID)
              }

              if (task.projectID && location.pathname.includes('ppf/projects/workspace')) {
                const nextStageID = res?.additionalResponseData?.NextInProgessTask?.StageID || null
                const stageIDs = nextStageID ? [task.stageID, nextStageID] : [task.stageID]
                useUpdateProjectStageSummary([...new Set(stageIDs)], task.projectID)
              }

              if (task.projectID && location.pathname.includes('ppf/planReviews')) {
                mutate(`Project/PlanReview/GetTaskSummary/${task.projectID}`);
              }

              if (location.pathname === '/ppf/taskList') {
                mutate((key: any) => typeof key === 'string' ? key.includes('Task/GetList') : Array.isArray(key) && key[0]?.includes('Task/GetList'));
              }

              mutate((key: any) => {
                if (!key) return false;
                const keyStr = typeof key === 'string' ? key : (Array.isArray(key) ? key[0] : JSON.stringify(key));
                return keyStr.includes('/TaskBoard') || keyStr.includes('Task/GetPanelList');
              });
            }}
          />
        )}

        {/* Update Elapsed Time (ET) Modal */}
        {openUpdateTAT && (
          <TATForm
            open={openUpdateTAT}
            onClose={() => setOpenUpdateTAT(false)}
            task={task}
            onSuccess={(updatedValues) => {
              if (updatedValues?.startedOn !== undefined) {
                task.startedOn = updatedValues.startedOn;
              }
              if (updatedValues?.completedOn !== undefined) {
                task.completedOn = updatedValues.completedOn;
              }
              if (updatedValues?.updatedOn) {
                setCurrentUpdatedOn(updatedValues.updatedOn);
                task.updatedOn = updatedValues.updatedOn;
                if (task.updatedBy) {
                  task.updatedBy.updatedDate = updatedValues.updatedOn;
                }
              }

              const derivedStatus = deriveStatusFromDates(task.startedOn, task.completedOn);
              if (derivedStatus !== 'Invalid') {
                setCurrentStatus(derivedStatus);
                task.status = derivedStatus;
              }
              setOpenUpdateTAT(false);

              // Revalidate SWR caches so all lists and drawers update immediately
              mutate((key: any) =>
                typeof key === 'string'
                  ? (key.includes('Task/GetPanelList') || key.includes('Task/GetList') || key.includes('/TaskBoard'))
                  : Array.isArray(key) && (key[0]?.includes('Task/GetPanelList') || key[0]?.includes('Task/GetList') || key[0]?.includes('/TaskBoard'))
              );

              if (task.projectID && location.pathname.includes("ppf/projects")) {
                useGetPPFProjectByID(task.projectID);
              }
              if (task.projectID && location.pathname.includes('ppf/projects/workspace')) {
                useUpdateProjectStageSummary([task.stageID], task.projectID);
              }
              if (task.projectID && location.pathname.includes('ppf/planReviews')) {
                mutate(`Project/PlanReview/GetTaskSummary/${task.projectID}`);
              }
            }}
          />
        )}

        {/* Delete Note Modal */}
        {openDeleteNote && (
          <DeleteNoteModal
            open={openDeleteNote}
            onClose={() => {
              setOpenDeleteNote(false);
              setDeletingNote(null);
            }}
            onConfirm={async () => {
              if (deletingNote) {
                const noteId = (deletingNote as any).taskNoteID || deletingNote.id;
                await useDeleteTaskNote(noteId);
                await mutateNotes();
              }
              setOpenDeleteNote(false);
              setDeletingNote(null);
            }}
            filesCount={deletingNote?.attachments?.length || 0}
          />
        )}

        {/* PDF Preview Modal */}
        {Boolean(pdfPreviewState) && (
          <PdfPreview
            pdfUrl={pdfPreviewState?.url || null}
            pdfTitle={pdfPreviewState?.title || 'PDF Preview'}
            onClose={() => setPdfPreviewState(null)}
          />
        )}
      </Stack>
    </Card>
  );
}
